'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classnames from 'classnames';
import * as React from 'react';
import { FieldNote, Image, Label, Legend, Radio, useUniqueId, } from '../../index.js';
const RadioFieldContext = React.createContext({});
export function RadioField(props) {
    const { defaultValue, selectedValue, onChange, variant, inverted, orientation, size, isError, disabled, hideLegend, label, optionalLabel, fieldNote, required, requiredLabel, iconName, children, className } = props, other = __rest(props, ["defaultValue", "selectedValue", "onChange", "variant", "inverted", "orientation", "size", "isError", "disabled", "hideLegend", "label", "optionalLabel", "fieldNote", "required", "requiredLabel", "iconName", "children", "className"]);
    const [valueState, setValueState] = React.useState(defaultValue);
    const isControlled = React.useRef(selectedValue !== undefined).current;
    const activeValue = isControlled ? selectedValue || defaultValue : valueState;
    const handleChangeRef = React.useRef(onChange);
    React.useEffect(() => {
        handleChangeRef.current = onChange;
    });
    const setActiveValue = (value) => {
        const update = handleChangeRef.current;
        if (isControlled) {
            onChange === null || onChange === void 0 ? void 0 : onChange(value);
        }
        else {
            setValueState(() => {
                const newVal = value;
                update === null || update === void 0 ? void 0 : update(newVal);
                return newVal;
            });
        }
    };
    const [idVar] = useUniqueId();
    const componentClassName = classnames('sparky-c-radio-field', className, {
        'sparky-c-radio-field--inline': orientation === 'inline',
        'sparky-c-radio-field--boxed': variant === 'boxed',
        'sparky-c-radio-field--toggle': variant === 'toggle',
        'sparky-c-radio-field--toggle-rounded': variant === 'toggle-rounded',
        'sparky-c-radio-field--sm': size === 'sm',
        'sparky-c-radio-field--inverted': inverted,
        'sparky-is-error': isError,
        'sparky-is-disabled': disabled,
    });
    return (_jsxs("fieldset", Object.assign({ className: componentClassName }, other, { children: [label && (_jsx(Legend, { className: "sparky-c-radio-field__label", hideLegend: hideLegend, text: label, legendSize: "sm", required: required, optionalLabel: optionalLabel, requiredLabel: requiredLabel, "aria-describedby": fieldNote ? idVar : undefined })), _jsx("div", Object.assign({ className: "sparky-c-radio-field__body" }, { children: _jsx("ul", Object.assign({ className: "sparky-c-radio-field__list" }, { children: _jsx(RadioFieldContext.Provider, Object.assign({ value: {
                            activeValue,
                            setActiveValue,
                            collectionId: idVar,
                            required,
                        } }, { children: React.Children.toArray(children).map((child, index) => {
                            if (React.isValidElement(child)) {
                                const newProps = {
                                    __index: index,
                                };
                                return React.cloneElement(child, newProps);
                            }
                            return child;
                        }) })) })) })), fieldNote ? (_jsx(FieldNote, Object.assign({ className: "sparky-c-radio-field__note", id: idVar, inverted: inverted, iconName: iconName, isError: isError }, { children: fieldNote }))) : undefined] })));
}
export const RadioFieldItem = React.forwardRef((props, ref) => {
    const { className, value, text, inverted = false, disabled = false, diagonalStrikethrough = false, centerAlign = false, hiddenContent, readOnly, itemAfter, itemBefore, imageSrc, imageAlt, __index, imageComponent, imageComponentProps } = props, other = __rest(props, ["className", "value", "text", "inverted", "disabled", "diagonalStrikethrough", "centerAlign", "hiddenContent", "readOnly", "itemAfter", "itemBefore", "imageSrc", "imageAlt", "__index", "imageComponent", "imageComponentProps"]);
    const [idVar] = useUniqueId();
    const [focused, setFocused] = React.useState(false);
    const { activeValue, setActiveValue, collectionId, required } = React.useContext(RadioFieldContext);
    const isChecked = activeValue === value;
    const tabIndex = (activeValue !== null && activeValue !== void 0 ? activeValue : __index === 0) || isChecked ? 0 : -1;
    const handleChange = (e) => {
        setActiveValue === null || setActiveValue === void 0 ? void 0 : setActiveValue(e.target.value);
    };
    const handleBlur = () => {
        setFocused(false);
    };
    const handleFocus = () => {
        setFocused(true);
    };
    const componentClassName = classnames('sparky-c-radio-field__item', className, {
        'sparky-c-radio-field__item--checked': isChecked,
        'sparky-c-radio-field__item--image': imageSrc,
        'sparky-c-radio-field__item--inverted': inverted === true,
        'sparky-c-radio-field__item--disabled': disabled === true,
        'sparky-c-radio-field__item--diagonal-strikethrough': diagonalStrikethrough === true,
        'sparky-c-radio-field__item--focused': focused === true,
        'sparky-c-radio-field__item--center-align': centerAlign === true,
    });
    return (_jsxs("li", Object.assign({ ref: ref, className: componentClassName }, other, { children: [_jsxs("div", Object.assign({ className: "sparky-c-radio-field__item-body" }, { children: [_jsx(Radio, { className: "sparky-c-radio-field__item-control", id: idVar, value: value, defaultChecked: isChecked, "aria-checked": isChecked, onChange: handleChange, onBlur: handleBlur, onFocus: handleFocus, disabled: disabled, readOnly: readOnly, inverted: inverted, required: required, tabIndex: tabIndex, name: collectionId }), itemBefore && (_jsx("div", Object.assign({ className: "sparky-c-radio-field__item-before" }, { children: itemBefore }))), _jsx(Label, Object.assign({ className: "sparky-c-radio-field__item-label sparky-c-tile", htmlFor: idVar, text: text }, { children: imageSrc && (_jsx(Image, Object.assign({ className: "sparky-c-radio-field_item--image__img", alt: imageAlt, src: imageSrc, alternativeComponent: imageComponent }, imageComponentProps))) }))] })), itemAfter && (_jsx("div", Object.assign({ className: "sparky-c-radio-field__item-after" }, { children: itemAfter }))), isChecked && hiddenContent ? (_jsx("div", Object.assign({ className: "sparky-c-radio-field__hidden-content" }, { children: hiddenContent }))) : undefined] })));
});
RadioFieldItem.displayName = 'RadioFieldItem';
//# sourceMappingURL=RadioField.js.map